import { useMemo } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import { faUsers, faUsersSlash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { deleteContactRelationshipById } from 'api/relationships';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Table from 'components/table';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import { useCurrentContactRelationships } from 'customHooks/useCurrentContactRelationships';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Relationship } from 'types/domain';

const relationshipColumnHelper = createColumnHelper<Relationship>();

const ContactRelationshipsTable = () => {
    const toast = useToast();
    const match = useRouteMatch();
    const history = useHistory();
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const { data: contact } = useCurrentContact();
    const { data: relationships = [] } = useCurrentContactRelationships();

    const { mutate: deleteRelationship } = useMutation({
        mutationFn: (relationshipId: number) =>
            deleteContactRelationshipById(accountId, contact.id, relationshipId),
        onSuccess: () => {
            toast.success(`Deleted relationship successfully!`);
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contactById(accountId, contact.id),
            });
        },
        onError: () => {
            toast.error(`Failed to delete relationship. Please try again.`);
        },
    });

    const columns = useMemo(
        () => [
            relationshipColumnHelper.accessor('related_contact', {
                header: 'Name',
                cell: info => {
                    const contact = info.getValue();

                    return <p className={'font-medium'}>{contact.name_display}</p>;
                },
            }),
            relationshipColumnHelper.accessor('related_type', {
                header: `Relation to ${contact.first_name_display}`,
                cell: info => {
                    return (
                        <p>
                            {`${info.row.original.related_contact.first_name_display} is ${contact.first_name_display}'s `}
                            <span className={'font-medium'}>{info.getValue()}</span>
                        </p>
                    );
                },
            }),
            relationshipColumnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    return (
                        <DropdownMenu.Root key={info.row.original.id}>
                            <Table.TableRowDropdownTrigger />
                            <DropdownMenu.Content sideOffset={5} align={'end'}>
                                <DropdownMenu.Item
                                    onClick={() =>
                                        history.push(
                                            `/accounts/${accountId}/contacts/${info.row.original.related_contact.id}`,
                                        )
                                    }
                                >
                                    View Contact
                                </DropdownMenu.Item>
                                <DropdownMenu.Item
                                    onClick={() =>
                                        history.push(`${match.url}/${info.row.original.id}/update`)
                                    }
                                >
                                    Edit relationship
                                </DropdownMenu.Item>
                                <AlertDialog.Root type={'error'}>
                                    <AlertDialog.Trigger asChild={true}>
                                        <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                                            Delete relationship
                                        </DropdownMenu.Item>
                                    </AlertDialog.Trigger>
                                    <AlertDialog.Portal>
                                        <AlertDialog.Overlay />
                                        <AlertDialog.Content>
                                            <AlertDialog.Header>
                                                <AlertDialog.Icon />
                                                <AlertDialog.Close />
                                            </AlertDialog.Header>
                                            <AlertDialog.Body>
                                                <AlertDialog.Title>
                                                    Delete relationship with{' '}
                                                    {info.row.original.related_contact.first_name}
                                                </AlertDialog.Title>
                                                <AlertDialog.Description>
                                                    You won't be able to undo this action. Are you
                                                    sure?
                                                </AlertDialog.Description>
                                            </AlertDialog.Body>
                                            <AlertDialog.Footer>
                                                <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                                                <AlertDialog.Action
                                                    onClick={() =>
                                                        deleteRelationship(info.row.original.id)
                                                    }
                                                >
                                                    Delete relationship
                                                </AlertDialog.Action>
                                            </AlertDialog.Footer>
                                        </AlertDialog.Content>
                                    </AlertDialog.Portal>
                                </AlertDialog.Root>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    );
                },
            }),
        ],
        [accountId, contact, deleteRelationship, history, match],
    );

    const table = useReactTable({
        data: relationships,
        columns,
        getCoreRowModel: getCoreRowModel(),
        enableSorting: false,
        enableHiding: false,
        enableColumnPinning: false,
    });

    return (
        <Box className="overflow-hidden">
            <div className={'flex items-center justify-between p-4'}>
                <div className={'flex items-center gap-2'}>
                    <FontAwesomeIcon className={'text-gray-600'} icon={faUsers} />
                    <p className={'text-lg font-medium text-gray-700'}>Relationships</p>
                </div>
                <Button
                    size={'sm'}
                    variant={'outline'}
                    color={'neutral'}
                    onClick={() => history.push(`${match.url}/create`)}
                >
                    + Add relationship
                </Button>
            </div>
            <Separator />
            <Table.Root table={table} variant="bordered">
                <Table.Content>
                    <Table.Header />
                    <Table.Body />
                </Table.Content>
                <Table.Empty variant={'secondary'}>
                    <Table.EmptyIcon>
                        <FontAwesomeIcon icon={faUsersSlash} />
                    </Table.EmptyIcon>
                    <Table.EmptyTitle>No relationships found</Table.EmptyTitle>
                    <Table.EmptyDescription>
                        Add a relationship to establish {contact.first_name}'s connections
                    </Table.EmptyDescription>
                </Table.Empty>
            </Table.Root>
        </Box>
    );
};

export { ContactRelationshipsTable };
